/** @prettier */

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  CircularProgress,
  Stack,
  Switch,
  Typography,
  Grid,
} from '@mui/material';
import {useState, useEffect} from 'react';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import TextFieldSelectBox from '../../component/SelectBox';
import APIErrorMessage from '../../component/APIErrorMessage';
//API import
import {
  patchCompositionsRTUnit,
  getCompositionsRTUnit,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {pullDownMenu} from '../../define';
import {sortByUnicode} from '../../functions/sort';

/**
 * 拡張設定ダイアログ
 * @param props props
 * @returns JSX.Element
 */
const RTAdvancedDialog = (props: {
  /** 修正する熱源機ID */
  unitID: string;
  /** 修正する熱源機の情報 */
  displayRTInfo: apiIF.resParameters.rtUnitInformations;
  /** 熱源機の情報（set関数） */
  setRTInfo: React.Dispatch<
    React.SetStateAction<apiIF.res.getCompositionsRTUnit | null>
  >;
  /** ダイアログを開くかどうか */
  isOpenDialog: boolean;
  /** ダイアログを開くかどうか（set関数） */
  setIsOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
  /** 課金しているかどうか */
  isBilling: boolean;
  /** GW毎の構成情報未反映の有無（set関数） */
  setHasConfigGW: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  //パスパラメータ取得
  const {siteID, gwID} = useParams();
  const unitID = props.unitID;

  const qtonNameplate =
    props.displayRTInfo['nameplate'] === undefined
      ? 'ESA30E-25'
      : props.displayRTInfo['nameplate'];
  const tempAdvancedSetting =
    props.displayRTInfo['advanced-setting'] === 'active';
  const tempUnitModel =
    props.displayRTInfo['unit-model'] === undefined
      ? 'WCMC'
      : props.displayRTInfo['unit-model'];
  // 未設定(空文字)の場合は旧ポンプを設定
  const tempPumpModel =
    props.displayRTInfo['pump-model'] === undefined ||
    props.displayRTInfo['pump-model'] === ''
      ? 'PD-51JU2'
      : props.displayRTInfo['pump-model'];

  const [advancedSetting, setAdvancedSetting] =
    useState<boolean>(tempAdvancedSetting);
  const [unitModel, setUnitModel] =
    useState<apiIF.reqParameters.patchCompositionsRTUnit['unit-model']>(
      tempUnitModel
    );
  const [pumpModel, setPumpModel] =
    useState<apiIF.reqParameters.patchCompositionsRTUnit['pump-model']>(
      tempPumpModel
    );

  const [okBtnDisabled, setOKBtnDisabled] = useState(false); //OKボタン非表示フラグ
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //APIエラー時のエラーダイアログ表示フラグ
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  const trans = useTranslation().t; //翻訳関数

  //入力項目のチェック
  useEffect(() => {
    if (apiWait) {
      setOKBtnDisabled(true);
    } else {
      setOKBtnDisabled(false);
    }
  }, [apiWait]);

  useEffect(() => {
    setUnitModel(
      props.displayRTInfo['unit-model'] === undefined
        ? 'WCMC'
        : props.displayRTInfo['unit-model']
    );
    // 未設定(空文字)の場合は旧ポンプを設定
    setPumpModel(
      props.displayRTInfo['pump-model'] === undefined ||
        props.displayRTInfo['pump-model'] === ''
        ? 'PD-51JU2'
        : props.displayRTInfo['pump-model']
    );
  }, [props.displayRTInfo]);

  /**
   * OKボタン押下時処理
   * @returns
   */
  const handleOK = () => {
    //パスパラメータを取得できない場合
    if (
      siteID === undefined ||
      gwID === undefined ||
      unitID === undefined ||
      unitModel === undefined ||
      pumpModel === undefined ||
      advancedSetting === undefined
    ) {
      return;
    }

    setAPIWait(true);

    //設定した内容を反映させる
    //APIに送信するデータを作成
    const editData: apiIF.reqParameters.patchCompositionsRTUnit = {
      'unit-model': unitModel,
      'pump-model': pumpModel,
      'advanced-setting': advancedSetting ? 'active' : 'inactive',
    };
    //情報を編集
    patchCompositionsRTUnit(siteID, gwID, unitID, editData)
      .then(() => {
        //室内外機の情報を取得
        return getCompositionsRTUnit(siteID, gwID, {'max-result': 400});
      })
      .then(response => {
        //表示内容をUnicode順に並び替え
        response['unit-informations'].sort(sortByUnicode('unit-name'));
        props.setRTInfo(response);
        props.setHasConfigGW(props.isBilling); //課金している場合のみ表示
        props.setIsOpenDialog(false);
      })
      .catch(() => {
        setIsOpenErrorDialog(true);
      })
      .finally(() => {
        setAPIWait(false);
      });
  };
  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    props.setIsOpenDialog(false);
  };

  /**
   * 拡張設定有効無効スイッチ選択時処理
   */
  const handleChange = () => {
    // 拡張設定が無効になった場合は銘版を基準に
    // デフォルト基板・ポンプの設定にする
    if (advancedSetting) {
      switch (qtonNameplate) {
        case 'ESA30E-25':
          setUnitModel('WCMC');
          setPumpModel('PD-51JU2');
          break;
        case 'ESA30EH-25':
          setUnitModel('WCMD');
          setPumpModel('PD-51JU2');
          break;
        case 'ESA30EH2-25':
          setUnitModel('WCME');
          setPumpModel('PDC88-510C');
          break;
      }
    }
    setAdvancedSetting(!advancedSetting);
  };

  return (
    <>
      <Dialog
        open={props.isOpenDialog}
        onClose={handleClose}
        aria-labelledby="advanced-dialog-title">
        <DialogTitle id="advanced-dialog-title">
          {trans('RTAdvancedDialog.dialogBox.title')}
        </DialogTitle>
        <DialogContent>
          {/** ダイアログに表示するメッセージ */}
          <DialogContentText>{trans('RTAdvancedDialog.msg')}</DialogContentText>
          <Stack spacing={3}>
            <div>
              <Grid container alignItems="center" spacing={2}>
                <Grid item xs={'auto'}>
                  {/* 拡張設定無効 */}
                  <Typography>
                    {trans('RTAdvancedDialog.advancedSetting.switchTxt')}
                  </Typography>
                </Grid>
                <Grid item xs={'auto'}>
                  <Switch onChange={handleChange} checked={advancedSetting} />
                </Grid>
              </Grid>
            </div>
            {/** 基板の種類 */}
            <TextFieldSelectBox
              choices={pullDownMenu.RT_UNIT_MODEL}
              value={unitModel}
              setValue={setUnitModel}
              disabled={!advancedSetting}
              label={trans(
                'RTAdvancedDialog.advancedSetting.label.softVersion'
              )}
              class={'gwSetting__contents__selectBox--rtModel'}
            />
            {/** ポンプのモデル */}
            <TextFieldSelectBox
              choices={pullDownMenu.RT_PUMP_MODEL}
              value={pumpModel}
              setValue={setPumpModel}
              disabled={!advancedSetting}
              label={trans('RTAdvancedDialog.advancedSetting.label.pumpModel')}
              class={'gwSetting__contents__selectBox--pumpModel'}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={apiWait}
            onClick={handleClose}
            color="primary"
            id={'mv-btnCancelEditIu'}>
            {trans('RTAdvancedDialog.buttonTxt.cancel')}
          </Button>
          <Button
            disabled={okBtnDisabled}
            color="primary"
            onClick={handleOK}
            id={'mv-btnOkEditIu'}>
            {apiWait ? (
              <CircularProgress size={20} />
            ) : (
              <>{trans('RTAdvancedDialog.buttonTxt.ok')}</>
            )}
          </Button>
        </DialogActions>
      </Dialog>
      <APIErrorMessage
        open={isOpenErrorDialog}
        setOpen={setIsOpenErrorDialog}
        id={`AdvancedSettingErrDlgBtn`}
      />
    </>
  );
};
export default RTAdvancedDialog;
